// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import type { AzureSupportedClouds } from "./static-helpers/cloudSettingHelpers.js";
import { AzureClouds } from "./static-helpers/cloudSettingHelpers.js";
import type {
  PageSettings,
  ContinuablePage,
  PagedAsyncIterableIterator,
} from "./static-helpers/pagingHelpers.js";

export { RecoveryServicesClient } from "./recoveryServicesClient.js";
export type { SimplePollerLike } from "./static-helpers/simplePollerHelpers.js";
export type { RestorePollerOptions } from "./restorePollerHelpers.js";
export { restorePoller } from "./restorePollerHelpers.js";
export type {
  OperationResource,
  ErrorModel,
  ErrorAdditionalInfo,
  CloudError,
  Vault,
  VaultProperties,
  UpgradeDetails,
  VaultUpgradeState,
  TriggerType,
  PrivateEndpointConnectionVaultProperties,
  PrivateEndpointConnection,
  ProvisioningState,
  PrivateEndpoint,
  PrivateLinkServiceConnectionState,
  PrivateEndpointConnectionStatus,
  VaultSubResourceType,
  VaultPrivateEndpointState,
  VaultPropertiesEncryption,
  CmkKeyVaultProperties,
  CmkKekIdentity,
  InfrastructureEncryptionState,
  VaultPropertiesMoveDetails,
  ResourceMoveState,
  BackupStorageVersion,
  PublicNetworkAccess,
  MonitoringSettings,
  AzureMonitorAlertSettings,
  AlertsState,
  ClassicAlertSettings,
  CostManagementSettings,
  GranularityLevel,
  RestoreSettings,
  CrossSubscriptionRestoreSettings,
  CrossSubscriptionRestoreState,
  VaultPropertiesRedundancySettings,
  StandardTierStorageRedundancy,
  CrossRegionRestore,
  SecuritySettings,
  ImmutabilitySettings,
  ImmutabilityState,
  ImmutabilityConfiguration,
  ImmutabilityType,
  SoftDeleteSettings,
  SoftDeleteState,
  EnhancedSecurityState,
  MultiUserAuthorization,
  SourceScanConfiguration,
  State,
  AssociatedIdentity,
  IdentityType,
  SecureScoreLevel,
  BcdrSecurityLevel,
  RegionOfChoiceSettings,
  IdentityData,
  ResourceIdentityType,
  UserIdentity,
  Sku,
  SkuName,
  TrackedResource,
  Resource,
  SystemData,
  CreatedByType,
  ClientDiscoveryValueForSingleApi,
  ClientDiscoveryDisplay,
  ClientDiscoveryForProperties,
  ClientDiscoveryForServiceSpecification,
  ClientDiscoveryForLogSpecification,
  ErrorDetail,
  PatchVault,
  PatchTrackedResource,
  ErrorResponse,
  DeletedVault,
  DeletedVaultProperties,
  ProxyResource,
  DeletedVaultUndeleteInput,
  DeletedVaultUndeleteInputProperties,
  PrivateLinkResource,
  PrivateLinkResourceProperties,
  CertificateRequest,
  RawCertificateData,
  AuthType,
  VaultCertificateResponse,
  ResourceCertificateDetails,
  ResourceCertificateDetailsUnion,
  ResourceCertificateAndAadDetails,
  ResourceCertificateAndAcsDetails,
  ReplicationUsage,
  MonitoringSummary,
  JobsSummary,
  VaultUsage,
  UsagesUnit,
  NameInfo,
  VaultExtendedInfoResource,
  VaultExtendedInfo,
  CheckNameAvailabilityParameters,
  CheckNameAvailabilityResult,
  ResourceCapabilities,
  CapabilitiesProperties,
  DNSZone,
  ResourceCapabilitiesBase,
  CapabilitiesResponse,
  CapabilitiesResponseProperties,
  DNSZoneResponse,
} from "./models/index.js";
export {
  KnownVaultUpgradeState,
  KnownTriggerType,
  KnownProvisioningState,
  KnownPrivateEndpointConnectionStatus,
  KnownVaultSubResourceType,
  KnownVaultPrivateEndpointState,
  KnownInfrastructureEncryptionState,
  KnownResourceMoveState,
  KnownBackupStorageVersion,
  KnownPublicNetworkAccess,
  KnownAlertsState,
  KnownGranularityLevel,
  KnownCrossSubscriptionRestoreState,
  KnownStandardTierStorageRedundancy,
  KnownCrossRegionRestore,
  KnownImmutabilityState,
  KnownImmutabilityType,
  KnownSoftDeleteState,
  KnownEnhancedSecurityState,
  KnownMultiUserAuthorization,
  KnownState,
  KnownIdentityType,
  KnownSecureScoreLevel,
  KnownBcdrSecurityLevel,
  KnownResourceIdentityType,
  KnownSkuName,
  KnownCreatedByType,
  KnownAuthType,
  KnownUsagesUnit,
  KnownVersions,
} from "./models/index.js";
export type {
  GetOperationResultOptionalParams,
  GetOperationStatusOptionalParams,
  RecoveryServicesClientOptionalParams,
} from "./api/index.js";
export type {
  DeletedVaultsGetOperationStatusOptionalParams,
  DeletedVaultsUndeleteOptionalParams,
  DeletedVaultsGetOptionalParams,
  DeletedVaultsListBySubscriptionIdOptionalParams,
} from "./api/deletedVaults/index.js";
export type { OperationsListOptionalParams } from "./api/operations/index.js";
export type {
  PrivateLinkResourcesListOptionalParams,
  PrivateLinkResourcesGetOptionalParams,
} from "./api/privateLinkResources/index.js";
export type {
  RecoveryServicesCapabilitiesOptionalParams,
  RecoveryServicesCheckNameAvailabilityOptionalParams,
} from "./api/recoveryServices/index.js";
export type { RegisteredIdentitiesDeleteOptionalParams } from "./api/registeredIdentities/index.js";
export type { ReplicationUsagesListOptionalParams } from "./api/replicationUsages/index.js";
export type { UsagesListByVaultsOptionalParams } from "./api/usages/index.js";
export type { VaultCertificatesCreateOptionalParams } from "./api/vaultCertificates/index.js";
export type {
  VaultExtendedInfoUpdateOptionalParams,
  VaultExtendedInfoCreateOrUpdateOptionalParams,
  VaultExtendedInfoGetOptionalParams,
} from "./api/vaultExtendedInfo/index.js";
export type {
  VaultsListByResourceGroupOptionalParams,
  VaultsListBySubscriptionIdOptionalParams,
  VaultsDeleteOptionalParams,
  VaultsUpdateOptionalParams,
  VaultsCreateOrUpdateOptionalParams,
  VaultsGetOptionalParams,
} from "./api/vaults/index.js";
export type {
  DeletedVaultsOperations,
  OperationsOperations,
  PrivateLinkResourcesOperations,
  RecoveryServicesOperations,
  RegisteredIdentitiesOperations,
  ReplicationUsagesOperations,
  UsagesOperations,
  VaultCertificatesOperations,
  VaultExtendedInfoOperations,
  VaultsOperations,
} from "./classic/index.js";
export type { PageSettings, ContinuablePage, PagedAsyncIterableIterator };
export { AzureClouds };
export type { AzureSupportedClouds };
export { RestError, isRestError } from "@azure/core-rest-pipeline";
